import axios from 'axios';
import { AutoComplete } from 'primereact/autocomplete';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { Button } from 'primereact/button';
import './Search.css';
import ChevronLink from '../common/ChevronLink/ChevronLink';
import { URLKeys } from '../../Constants';

type Props = {
    open: boolean;
    setOpen: (open: boolean) => void;
};

const Search = ({ open, setOpen }: Props) => {
    const [value, setValue] = useState<string>('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const { municipalitaId, bodyType, zastupitelstvoId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const autocompleteSearch = async (event: { query: string }) => {
        const response = await axios.get(`/${municipalitaId}/${bodyType}/${zastupitelstvoId}/search`, {
            params: { search: event.query }
        });
        const responseData = response.data;

        const members = responseData.members || [];
        const parties = responseData.parties || [];
        const votings = responseData.votings || [];
        const membersLength = members.length;
        const partiesLength = parties.length;
        const votingsLength = votings.length;

        const MAX_ITEMS = 6;
        const ITEMS_PER_GROUP = 3;

        let membersCount = Math.min(ITEMS_PER_GROUP, membersLength);
        let partiesCount = Math.min(ITEMS_PER_GROUP, partiesLength);

        if (membersCount === 0 && partiesCount > 0) {
            partiesCount = Math.min(MAX_ITEMS, partiesLength);
        } else if (partiesCount === 0 && membersCount > 0) {
            membersCount = Math.min(MAX_ITEMS, membersLength);
        } else if (membersCount + partiesCount < MAX_ITEMS) {
            const remaining = MAX_ITEMS - (membersCount + partiesCount);
            if (membersLength > membersCount) {
                membersCount = Math.min(membersCount + remaining, membersLength);
            } else if (partiesLength > partiesCount) {
                partiesCount = Math.min(partiesCount + remaining, partiesLength);
            }
        }

        const newSuggestions = [];
        if (membersCount > 0) {
            newSuggestions.push({
                label: t('Members'),
                count: membersLength,
                items: [...members.slice(0, membersCount), { path: 'zastupitele' }]
            });
        }
        if (partiesCount > 0) {
            newSuggestions.push({
                label: t('Parties'),
                count: partiesLength,
                items: [...parties.slice(0, partiesCount), { path: 'subjekty' }]
            });
        }
        if (votingsLength > 0) {
            newSuggestions.push({
                label: t('Votings'),
                count: votingsLength,
                items: [{ path: `hlasovani?${URLKeys.voteDescription}=${event.query}#voting-cards` }]
            });
        }

        if (newSuggestions.length === 0) {
            newSuggestions.push({
                label: t('search-no-results'),
                items: []
            });
        }

        setSuggestions(newSuggestions);
    };

    const itemTemplate = (item: any) => {
        if (item?.path !== undefined) {
            return <ChevronLink chevronDirection='right' text={t('display-all')} linkDisabled />;
        }
        return (
            <div className='search-item'>
                {item?.party_name}
                {item?.jmeno} {item?.prijmeni}
                {item?.predmet}
            </div>
        );
    };

    const groupedItemTemplate = (item: any) => {
        return (
            <>
                <div>{item?.label}</div>
                {item?.count > 0 && <div>{t('total-results', { totalCount: item?.count, count: item?.count })}</div>}
            </>
        );
    };

    const navigateToSelected = (item: any) => {
        if (item?.party_name !== undefined) {
            navigate(`/${municipalitaId}/${bodyType}/${zastupitelstvoId}/subjekty/${item?.party_id}`);
        }
        if (item?.predmet !== undefined) {
            navigate(`/${municipalitaId}/${bodyType}/${zastupitelstvoId}/hlasovani/${item?.id}`);
        }
        if (item?.jmeno !== undefined) {
            navigate(`/${municipalitaId}/${bodyType}/${zastupitelstvoId}/zastupitele/${item?.id}`);
        }
        if (item?.path !== undefined) {
            navigate(`/${municipalitaId}/${bodyType}/${zastupitelstvoId}/${item?.path}`);
        }
    };

    return (
        <div className='search-wrapper'>
            {open && (
                <AutoComplete
                    value={value}
                    suggestions={suggestions}
                    completeMethod={autocompleteSearch}
                    field='label'
                    optionGroupLabel='label'
                    optionGroupChildren='items'
                    optionGroupTemplate={groupedItemTemplate}
                    autoFocus
                    placeholder={t('search-placeholder')}
                    onBlur={() => {
                        setTimeout(() => {
                            setOpen(false);
                            setValue('');
                        }, 200);
                    }}
                    itemTemplate={itemTemplate}
                    onChange={(e) => {
                        if (typeof e.value === 'object') {
                            navigateToSelected(e.value);
                            setOpen(false);
                            setValue('');
                        } else {
                            return setValue(e.value);
                        }
                    }}
                />
            )}
            {!open && (
                <Button
                    icon='pi pi-search'
                    className='p-button-rounded p-button-text'
                    aria-label='Search'
                    onClick={() => {
                        setOpen(!open);
                    }}
                />
            )}
        </div>
    );
};

export default Search;
